import { Box, Checkbox, Grid, Tooltip, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { IntlShape } from 'react-intl';
import { assign } from 'xstate';

import { MachineContext } from '../../MachineContext';
import { SendContext } from '../../SendContext';
import theme from '../../theme/theme';
import {
  getTranslatedObjects,
  getTranslatedTexts,
  translateWithFallback,
} from '../../util';
import { EvoachButton } from '../Button';
import { CoachMessage } from '../CoachMessage';
import { BaseComponent } from '../Component';
import { MultipleInput } from '../MultipleInput';

import { EmotionEnum } from './EmotionEnum';
import { EmotionIcon } from './EmotionIcon';

export const evoachEmotions = [
  {
    type: 'emotioninput',
    icon: EmotionEnum.EXCITED,
    value: 'excited',
  },
  { type: 'emotioninput', icon: EmotionEnum.HAPPY, value: 'Glücklich' },
  { type: 'emotioninput', icon: EmotionEnum.CONTENT, value: 'Fröhlich' },
  { type: 'emotioninput', icon: EmotionEnum.GRATEFUL, value: 'Dankbar' },
  { type: 'emotioninput', icon: EmotionEnum.TIRED, value: 'Müde' },
  { type: 'emotioninput', icon: EmotionEnum.ARROGANT, value: 'Arrogant' },
  { type: 'emotioninput', icon: EmotionEnum.ANGRY, value: 'Wütend' },
  { type: 'emotioninput', icon: EmotionEnum.SAD, value: 'Traurig' },
  { type: 'emotioninput', icon: EmotionEnum.STRESSED, value: 'Gestresst' },
  { type: 'emotioninput', icon: EmotionEnum.SCARED, value: 'Verängstigt' },
  { type: 'emotioninput', icon: EmotionEnum.CONFUSED, value: 'Verwirrt' },
  {
    type: 'emotioninput',
    icon: EmotionEnum.JUDGEMENTAL,
    value: 'Wertend',
  },
  { type: 'emotioninput', icon: EmotionEnum.ASHAMED, value: 'Beschämt' },
  { type: 'emotioninput', icon: EmotionEnum.HELPLESS, value: 'Hilflos' },
  { type: 'emotioninput', icon: EmotionEnum.CRYING, value: 'Weinend' },
  { type: 'emotioninput', icon: EmotionEnum.EXHAUSTED, value: 'Erschöpft' },
  { type: 'emotioninput', icon: EmotionEnum.FUMING, value: 'Kochend' },
  {
    type: 'emotioninput',
    icon: EmotionEnum.INDIFFERENT,
    value: 'Gleichgültig',
  },
  { type: 'emotioninput', icon: EmotionEnum.ANXIOUS, value: 'Besorgt' },
  { type: 'emotioninput', icon: EmotionEnum.HURT, value: 'Verletzt' },
  { type: 'emotioninput', icon: EmotionEnum.PROUD, value: 'Stolz' },
  { type: 'emotioninput', icon: EmotionEnum.EMPTY, value: 'Leer' },
  { type: 'emotioninput', icon: EmotionEnum.COLD, value: 'Kühl' },
  { type: 'emotioninput', icon: EmotionEnum.DOWN, value: 'Niedergeschlagen' },
  { type: 'emotioninput', icon: EmotionEnum.ANNOYED, value: 'Genervt' },
  { type: 'emotioninput', icon: EmotionEnum.CONTEMPT, value: 'Verärgert' },
  { type: 'emotioninput', icon: EmotionEnum.DISGUSTED, value: 'Angewidert' },
  { type: 'emotioninput', icon: EmotionEnum.SURPRISED, value: 'Überrascht' },
];

export interface EmotionInputComponent extends BaseComponent {
  type: 'emotionInput';
  props: EmotionInputProps;
}

export interface EmotionInputItem {
  type: string;
  icon: EmotionEnum;
  value: string;
  starred?: boolean;
}

export interface EmotionInputProps {
  buttonText: string;
  saveResultTo: string;
  placeholderTexts: string[];
  presetValues?: string[];
  elements: EmotionInputItem[];
  minItems?: number;
  maxItems?: number;
  hideOwnInput?: boolean;
  selectAll?: boolean;
}

// a little function to help us with reordering the result

const selectItems = (
  list: EmotionInputItem[],
  selectAll: boolean | undefined
): EmotionInputItem[] => {
  if (selectAll)
    list.forEach((value) => {
      value.starred = true;
    });
  return list;
};

const starredItemsLength = (list: EmotionInputItem[]): number => {
  let result = 0;
  list.forEach((value) => {
    if (value.starred && value.starred === true) result++;
  });

  return result;
};

const nonEmptySnippets = (snippets: string[]): number => {
  return snippets.filter((snip) => snip && snip.trim() !== '').length;
};

export const EmotionInput: React.FC<EmotionInputProps> = ({
  buttonText,
  saveResultTo,
  placeholderTexts,
  presetValues = [''],
  elements,
  minItems = 1,
  maxItems = 3,
  hideOwnInput = false,
  selectAll = false,
}) => {
  const { send } = useContext(SendContext);
  const [snippets, setSnippets] = useState<string[]>(
    presetValues ? presetValues : []
  );
  const [items, setItems] = useState<EmotionInputItem[]>(
    selectItems(elements.flat(), selectAll)
  );

  useEffect(() => {
    setItems(selectItems(elements.flat(), selectAll));
  }, [elements, selectAll]);

  return (
    <Box width="500px">
      <Grid container spacing={1} columns={{ xs: 15 }} marginBottom="15px">
        {items.map((item, index) => {
          return (
            <Grid item key={index} xs={3}>
              <Tooltip title={item.value} arrow placement="top">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textOverflow="clip"
                  sx={{
                    borderRadius: '5%',
                    backgroundColor: `${
                      item.starred
                        ? theme.palette.secondary.main
                        : theme.palette.success.contrastText
                    }`,
                    '&:hover': {
                      backgroundColor: `${
                        item.starred
                          ? theme.palette.secondary.dark
                          : theme.palette.secondary.light
                      }`,
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => {
                    const newItems = cloneDeep(items);
                    newItems[index].starred = !newItems[index].starred;

                    setItems(newItems);
                  }}
                >
                  <Checkbox
                    sx={{ paddingBottom: '0px' }}
                    icon={
                      <EmotionIcon emotion={item.icon} active={item.starred} />
                    }
                    checkedIcon={
                      <EmotionIcon emotion={item.icon} active={item.starred} />
                    }
                    disableRipple
                  />
                  <Typography
                    fontSize="0.9em"
                    sx={{
                      color: `${
                        item.starred
                          ? theme.palette.success.contrastText
                          : theme.palette.secondary.main
                      }`,
                      maxWidth: '10ch',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
      {!hideOwnInput ? (
        <MultipleInput
          buttonText="next"
          saveResultTo="x"
          showButton={false}
          placeholderTexts={placeholderTexts}
          presetValues={presetValues}
          onChange={(snippets: string[]) => {
            setSnippets(snippets);
          }}
        />
      ) : (
        ''
      )}

      <EvoachButton
        disabled={
          (minItems !== undefined &&
            minItems >
              starredItemsLength(items) + nonEmptySnippets(snippets)) ||
          (maxItems !== undefined &&
            maxItems < starredItemsLength(items) + nonEmptySnippets(snippets))
        }
        onClick={() => {
          const starredItems = items.filter(
            (item) => item.starred !== undefined && item.starred === true
          );

          let snippetItems: EmotionInputItem[] = [];
          for (let i = 0; i < snippets.length; i++) {
            if (snippets[i] !== '') {
              snippetItems.push({
                type: 'emotionInputOwnInput',
                value: snippets[i],
                icon: EmotionEnum.EMPTY,
                starred: true,
              });
            }
          }

          send({
            type: 'writeToContext',
            payload: {
              [saveResultTo]: starredItems.concat(snippetItems),
            },
          });
          send({ type: 'next' });
        }}
      >
        {buttonText}
      </EvoachButton>
    </Box>
  );
};

/*
  ! Composite component that displays a CoachMessage before the snippet input
*/

export interface EmotionInputCompositeProps extends EmotionInputProps {
  message: string;
}
export interface EmotionInputCompositeComponent extends BaseComponent {
  type: 'emotionInputComposite';
  props: EmotionInputCompositeProps;
}

export const EmotionInputComposite: React.FC<EmotionInputCompositeProps> = ({
  buttonText,
  saveResultTo,
  placeholderTexts,
  presetValues,
  message,
  elements,
  hideOwnInput,
  selectAll,
  minItems,
  maxItems,
}) => {
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <CoachMessage message={message} />
      </div>
      <EmotionInput
        buttonText={buttonText}
        saveResultTo={saveResultTo}
        placeholderTexts={placeholderTexts}
        presetValues={presetValues}
        elements={elements}
        hideOwnInput={hideOwnInput}
        selectAll={selectAll}
        minItems={minItems}
        maxItems={maxItems}
      />
    </>
  );
};

/** generate list of elements to be displayed */
const mapElements = (
  dataSourceList: string[],
  context: MachineContext,
  payloadElements: Record<string, any>[],
  payload: any
) =>
  dataSourceList
    ? dataSourceList.map((dataSource: string) => {
        const contextData = context.userData[dataSource];

        // when payloadELements exists and is not empty
        if (payloadElements && payloadElements.length > 0) {
          const mappedData = payloadElements.map((element: any) => {
            const valueExists = contextData
              ? contextData.reduce(
                  (exists: any, mappedElement: any) => {
                    if (mappedElement.value === element.value) {
                      return { exist: true, starred: mappedElement.starred };
                    } else {
                      return exists;
                    }
                  },
                  { exist: false, starred: false }
                )
              : { exist: false, starred: false };

            return {
              type: element.type,
              value: element.value,
              starred: valueExists.exist
                ? valueExists.starred
                : element.starred,
              icon: element.icon,
            };
          });

          // now find all elements that are in mappedData but not in
          // payloadElements ==> these are the user inputs

          const userInputs = contextData
            ? contextData.filter(
                (element: any) => element.type === 'emotionInputOwnInput'
              )
            : [];

          return [...mappedData, ...userInputs];
        } else {
          return contextData;
        }
      })
    : payload.elements;

/** render an emotion input as composite with message */
export const renderEmotionInputCompositeAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const translatedplaceholdertexts = getTranslatedTexts(
      payload.placeholderTexts,
      intl
    );

    // we expect payload elements to be an array of objects,
    let payloadElements = payload.elements;
    // if elements are passed, translate them as they are preset
    // note: elements may be an array of arrays of elements!
    if (payloadElements) {
      payloadElements = getTranslatedObjects(payloadElements, intl);
    } else {
      payloadElements = [];
    }

    const elements = mapElements(
      payload.getValuesFrom,
      context,
      payloadElements,
      payload
    );

    newContext.widgetData.push({
      type: 'emotionInputComposite',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        maxItems: payload.maxItems,
        minItems: payload.minItems,
        placeholderTexts: translatedplaceholdertexts,
        presetValues: payload.presetValues,
        hideOwnInput: payload.hideOwnInput,
        selectAll: payload.selectAll,
        message: payload.message
          ? translateWithFallback(intl, payload.message)
          : '',
      },
    });

    return newContext;
  });
};

/** render an Emotion Input */
export const renderEmotionInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const translatedplaceholdertexts = getTranslatedTexts(
      payload.placeholderTexts,
      intl
    );

    // we expect payload elements to be an array of objects,
    let payloadElements = payload.elements;
    // if elements are passed, translate them as they are preset
    // note: elements may be an array of arrays of elements!
    if (payloadElements) {
      payloadElements = getTranslatedObjects(payloadElements, intl);
    } else {
      payloadElements = [];
    }

    const elements = mapElements(
      payload.getValuesFro,
      context,
      payloadElements,
      payload
    );

    newContext.widgetData.push({
      type: 'emotionInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        maxItems: payload.maxItems,
        minItems: payload.minItems,
        placeholderTexts: translatedplaceholdertexts,
        presetValues: payload.presetValues,
        hideOwnInput: payload.hideOwnInput,
        selectAll: payload.selectAll,
      },
    });
    return newContext;
  });
};
